import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/homeData';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const toggleMenu = () => setMobileMenuOpen((prev) => !prev);
  const closeMenu = () => setMobileMenuOpen(false);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled || mobileMenuOpen
            ? 'bg-background-50/95 backdrop-blur-xl border-b border-foreground-200/30'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-[72px]">
            <Link
              to="/"
              className="flex items-center gap-2 group flex-shrink-0"
              onClick={closeMenu}
            >
              <span className="font-heading text-xl md:text-2xl font-bold text-foreground-900 tracking-tight">
                Superbuy Spreadsheet
              </span>
              <span className="w-2 h-2 rounded-full bg-primary-500 mt-1"></span>
            </Link>

            <div className="flex items-center gap-3">
              <div className="relative hidden md:block">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-foreground-200/50 bg-background-50/70 backdrop-blur-sm text-sm font-medium text-foreground-700 hover:border-foreground-300 transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  Shop Categories
                  <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {dropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setDropdownOpen(false)}
                    ></div>
                    <div className="absolute top-full mt-2 right-0 w-64 bg-background-50 rounded-xl border border-foreground-200/70 overflow-hidden z-50 animate-[fadeIn_0.15s_ease-out]">
                      <div className="py-2 max-h-[420px] overflow-y-auto">
                        {categories.map((cat) => (
                          <a
                            key={cat.name}
                            href={cat.url}
                            target="_blank"
                            rel="noopener nofollow"
                            onClick={() => setDropdownOpen(false)}
                            className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors duration-150 whitespace-nowrap"
                          >
                            {cat.name}
                          </a>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="hidden sm:inline-flex items-center px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Superbuy Sheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="hidden sm:inline-flex items-center px-5 py-2.5 rounded-full bg-foreground-900 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>

              <button
                onClick={toggleMenu}
                className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg border border-foreground-200/60 text-foreground-700 hover:bg-background-100 transition-colors duration-200 cursor-pointer"
                aria-label="Toggle menu"
              >
                <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 pt-16 bg-background-50 z-[100] overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeMenu();
          }}
        >
          <div className="px-4 py-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex flex-col gap-3 mb-8">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMenu}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Superbuy Sheet
                <i className="ri-external-link-line text-sm"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMenu}
                className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-foreground-900 text-background-50 text-sm font-semibold hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Buy It Now
                <i className="ri-shopping-bag-line text-sm"></i>
              </a>
            </div>

            <div className="mb-8">
              <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-4 px-1">
                Shop by Category
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={closeMenu}
                    className="flex items-center gap-3 p-3 rounded-xl bg-background-100 border border-foreground-200/40 hover:border-foreground-300/60 transition-all duration-200 cursor-pointer group"
                  >
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-background-200">
                      <img
                        src={cat.image}
                        alt={cat.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <span className="text-sm font-medium text-foreground-700 group-hover:text-foreground-900 transition-colors">
                      {cat.name}
                    </span>
                  </a>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider mb-4 px-1">
                SEO Guides
              </h3>
              <div className="flex flex-col gap-1">
                <Link
                  to="/article/superbuy-spreadsheet-complete-guide"
                  onClick={closeMenu}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-all duration-150 cursor-pointer"
                >
                  <i className="ri-book-open-line text-base text-foreground-400"></i>
                  Complete Guide
                </Link>
                <Link
                  to="/article/superbuy-spreadsheet-shoes-buying-guide"
                  onClick={closeMenu}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-all duration-150 cursor-pointer"
                >
                  <i className="ri-footprint-line text-base text-foreground-400"></i>
                  Shoes Buying Guide
                </Link>
                <Link
                  to="/article/superbuy-spreadsheet-shipping-comparison"
                  onClick={closeMenu}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-all duration-150 cursor-pointer"
                >
                  <i className="ri-ship-line text-base text-foreground-400"></i>
                  Shipping Comparison
                </Link>
                <Link
                  to="/article/superbuy-spreadsheet-beginners-handbook"
                  onClick={closeMenu}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-all duration-150 cursor-pointer"
                >
                  <i className="ri-user-smile-line text-base text-foreground-400"></i>
                  Beginner Handbook
                </Link>
                <Link
                  to="/article/superbuy-spreadsheet-safety-scam-prevention"
                  onClick={closeMenu}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-100 transition-all duration-150 cursor-pointer"
                >
                  <i className="ri-shield-check-line text-base text-foreground-400"></i>
                  Safety Guide
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}